import { GeneralDetailsFormData } from '../../components/offers/general/form/details/general';
import { CustomerDetailsFormData } from '../../components/offers/general/form/details/customer';
import { FreightDetailsFormData } from '../../components/offers/general/form/details/freight';
import { AddressDetailsFormData } from '../../components/offers/general/form/details/address';
import GeneralService from '../general';

export interface ISavedOfferHistoryData {
    savedAt: string;
    orderDetails?: GeneralDetailsFormData;
    customerDetails?: CustomerDetailsFormData;
    freightDetails?: FreightDetailsFormData;
    addressDetails?: AddressDetailsFormData;
}

const STORAGE_KEY = 'jg_last_offer_step_history';

const OfferStorageService = {
    saveStep1: (orderDetails?: GeneralDetailsFormData, customerDetails?: CustomerDetailsFormData) => {
        try {
            const existing = OfferStorageService.get() || { savedAt: new Date().toISOString() };
            const updated: ISavedOfferHistoryData = {
                ...existing,
                savedAt: new Date().toISOString(),
                orderDetails: orderDetails || existing.orderDetails,
                customerDetails: customerDetails || existing.customerDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to save offer step 1 to localStorage', e);
        }
    },

    saveStep2: (freightDetails?: FreightDetailsFormData, addressDetails?: AddressDetailsFormData) => {
        try {
            const existing = OfferStorageService.get() || { savedAt: new Date().toISOString() };
            const updated: ISavedOfferHistoryData = {
                ...existing,
                savedAt: new Date().toISOString(),
                freightDetails: freightDetails || existing.freightDetails,
                addressDetails: addressDetails || existing.addressDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to save offer step 2 to localStorage', e);
        }
    },

    saveAll: (
        orderDetails?: GeneralDetailsFormData,
        customerDetails?: CustomerDetailsFormData,
        freightDetails?: FreightDetailsFormData,
        addressDetails?: AddressDetailsFormData
    ) => {
        try {
            const data: ISavedOfferHistoryData = {
                savedAt: new Date().toISOString(),
                orderDetails,
                customerDetails,
                freightDetails,
                addressDetails,
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.error('Failed to save offer data to localStorage', e);
        }
    },

    get: (): ISavedOfferHistoryData | null => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as ISavedOfferHistoryData;
            return parsed;
        } catch (e) {
            console.error('Failed to parse offer history from localStorage', e);
            return null;
        }
    },

    hasData: (): boolean => {
        const data = OfferStorageService.get();
        if (!data) return false;
        const hasStep1 = !!(data.orderDetails?.subsidiary || data.customerDetails?.company);
        const hasStep2 = !!(data.freightDetails?.freightCategory || (data.addressDetails?.originAddresses && data.addressDetails.originAddresses.length > 0));
        return hasStep1 || hasStep2;
    },

    clear: () => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to clear offer history from localStorage', e);
        }
    },

    /**
     * Clones address details and assigns fresh GUIDs so that they are treated
     * as new records rather than colliding with previously submitted records.
     */
    prepareClonedAddressDetails: (addressDetails?: AddressDetailsFormData): AddressDetailsFormData | undefined => {
        if (!addressDetails) return undefined;
        return {
            originAddresses: (addressDetails.originAddresses || []).map((addr, idx) => ({
                ...addr,
                id: GeneralService.guid(),
                orderNumber: idx + 1,
            })),
            destinationAddresses: (addressDetails.destinationAddresses || []).map((addr, idx) => ({
                ...addr,
                id: GeneralService.guid(),
                orderNumber: idx + 1,
            })),
        };
    }
};

export default OfferStorageService;
